import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DatePicker, Select, Button, Spin, Empty, Progress, Tag, Card, App,
  Row, Col, Statistic,
} from 'antd';
import { Download, Filter, FileSpreadsheet, TrendingUp } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';
import * as XLSX from 'xlsx';
import { PageHeader } from '@/components/common/PageHeader';
import { kpiReportService, type KpiReportRow } from '@/services/taskService';
import { userService } from '@/services/userService';
import { createCategoryService } from '@/services/categoryService';
import type { User } from '@/types';

const deptService = createCategoryService<any>('departments');

/**
 * V1.0.30: Trang báo cáo KPI Doanh thu.
 *
 * Hiển thị các task ROOT có KPI plan > 0, kèm:
 * - Tổng aggregated actual (sum của task + tất cả descendant)
 * - % progress
 * - Filter theo khoa, người thực hiện, khoảng thời gian (theo due_date)
 * - Export Excel
 * - Click row → mở task detail
 */
export function KpiReportPage() {
  const navigate = useNavigate();
  const { message } = App.useApp();

  const [filterDept, setFilterDept] = useState<string | undefined>();
  const [filterGroup, setFilterGroup] = useState<string | undefined>(); // V1.0.33
  const [filterAssignee, setFilterAssignee] = useState<string | undefined>();
  const [filterFrom, setFilterFrom] = useState<Dayjs | null>(null);
  const [filterTo, setFilterTo] = useState<Dayjs | null>(null);

  const [users, setUsers] = useState<User[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [groups, setGroups] = useState<any[]>([]); // V1.0.33

  useEffect(() => {
    (async () => {
      try {
        const [usersResp, depts] = await Promise.all([
          userService.list({ pageSize: 200 }),
          deptService.list().catch(() => []),
        ]);
        setUsers(usersResp.items);
        setDepartments(depts);
      } catch {}
    })();
    // V1.0.33: Load groups
    import('@/services/rolesService').then(m => {
      m.orgService.listGroups().then(setGroups).catch(() => {});
    });
  }, []);

  // V1.0.33: Filter departments dropdown theo nhóm
  const filteredDepartments = useMemo(() => {
    if (!filterGroup) return departments;
    return departments.filter((d: any) => d.groupId === filterGroup);
  }, [departments, filterGroup]);

  // Khi đổi nhóm → clear filterDept nếu không thuộc nhóm mới
  useEffect(() => {
    if (filterGroup && filterDept) {
      const inGroup = departments.find((d: any) => d.id === filterDept);
      if (inGroup && inGroup.groupId !== filterGroup) {
        setFilterDept(undefined);
      }
    }
  }, [filterGroup, departments]);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['kpi-report', filterDept, filterAssignee, filterFrom?.format('YYYY-MM-DD'), filterTo?.format('YYYY-MM-DD')],
    queryFn: () => kpiReportService.getReport({
      departmentId: filterDept,
      assigneeUserId: filterAssignee,
      from: filterFrom?.format('YYYY-MM-DD'),
      to: filterTo?.format('YYYY-MM-DD'),
    }),
  });

  const rows: KpiReportRow[] = data ?? [];

  // Aggregate cards
  const totals = useMemo(() => {
    const totalPlan = rows.reduce((s, r) => s + r.kpiRevenuePlan, 0);
    const totalActual = rows.reduce((s, r) => s + r.aggregatedKpiActual, 0);
    const avgPct = rows.length > 0
      ? rows.reduce((s, r) => s + r.progressPercent, 0) / rows.length
      : 0;
    const completed = rows.filter(r => r.progressPercent >= 100).length;
    return { totalPlan, totalActual, avgPct, completed, taskCount: rows.length };
  }, [rows]);

  const handleExport = () => {
    if (rows.length === 0) {
      message.warning('Không có dữ liệu để xuất');
      return;
    }
    const data = rows.map(r => ({
      'Mã task': r.code,
      'Tên task': r.title,
      'Khoa/phòng': r.departmentName,
      'Người thực hiện': r.assigneeName ?? '',
      'Trạng thái': r.status,
      'Hạn': dayjs(r.dueDate).format('DD/MM/YYYY'),
      'KPI Kế hoạch (VND)': r.kpiRevenuePlan,
      'KPI Đã đạt (VND)': r.aggregatedKpiActual,
      'Phần trăm đạt': `${r.progressPercent.toFixed(1)}%`,
      'Số task con': r.descendantCount,
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'KPI Doanh thu');
    const today = dayjs().format('YYYYMMDD');
    XLSX.writeFile(wb, `bao-cao-kpi-doanh-thu-${today}.xlsx`);
    message.success('Đã xuất file Excel');
  };

  const colorByPct = (pct: number): string => {
    if (pct >= 100) return '#52c41a';
    if (pct >= 70) return '#1976D2';
    if (pct >= 40) return '#fa8c16';
    return '#cf1322';
  };

  return (
    <>
      <PageHeader
        breadcrumb="BÁO CÁO · KPI DOANH THU"
        title="Báo cáo KPI Doanh thu"
        subtitle="Tổng hợp KPI của các task gốc có đặt KPI doanh thu. Hiển thị thực tế đạt được = tổng từ task gốc + toàn bộ task con/cháu."
      />

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="bottom">
          {/* V1.0.33: Filter theo nhóm khoa phòng */}
          <Col xs={24} md={5}>
            <div style={{ fontSize: 12, color: '#48535e', marginBottom: 4 }}>Nhóm khoa phòng</div>
            <Select
              allowClear
              placeholder="Tất cả nhóm"
              style={{ width: '100%' }}
              value={filterGroup}
              onChange={setFilterGroup}
              showSearch
              optionFilterProp="label"
              options={groups.filter((g: any) => g.isActive).map((g: any) => ({
                value: g.id,
                label: g.name,
              }))}
            />
          </Col>
          <Col xs={24} md={5}>
            <div style={{ fontSize: 12, color: '#48535e', marginBottom: 4 }}>Khoa/phòng</div>
            <Select
              allowClear
              placeholder={filterGroup ? "(theo nhóm đã chọn)" : "Tất cả khoa/phòng"}
              style={{ width: '100%' }}
              value={filterDept}
              onChange={setFilterDept}
              showSearch
              optionFilterProp="label"
              options={filteredDepartments.map((d: any) => ({ value: d.id, label: d.name }))}
            />
          </Col>
          <Col xs={24} md={5}>
            <div style={{ fontSize: 12, color: '#48535e', marginBottom: 4 }}>Người thực hiện</div>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Tất cả người dùng"
              style={{ width: '100%' }}
              value={filterAssignee}
              onChange={setFilterAssignee}
              options={users.map(u => ({ value: u.id, label: u.fullName }))}
            />
          </Col>
          <Col xs={24} md={4}>
            <div style={{ fontSize: 12, color: '#48535e', marginBottom: 4 }}>Hạn từ</div>
            <DatePicker
              style={{ width: '100%' }}
              value={filterFrom}
              onChange={setFilterFrom}
              format="DD/MM/YYYY"
              placeholder="Từ ngày"
            />
          </Col>
          <Col xs={24} md={4}>
            <div style={{ fontSize: 12, color: '#48535e', marginBottom: 4 }}>Hạn đến</div>
            <DatePicker
              style={{ width: '100%' }}
              value={filterTo}
              onChange={setFilterTo}
              format="DD/MM/YYYY"
              placeholder="Đến ngày"
            />
          </Col>
          <Col xs={24} md={1}>
            <Button
              type="primary"
              icon={<Filter size={14} />}
              onClick={() => refetch()}
              style={{ width: '100%' }}
              title="Lọc"
            />
          </Col>
        </Row>
      </Card>

      {/* Summary stats */}
      <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="Số task có KPI"
              value={totals.taskCount}
              prefix={<TrendingUp size={14} style={{ verticalAlign: -2 }} />}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="Tổng KPI kế hoạch"
              value={totals.totalPlan}
              formatter={(v) => `${Number(v).toLocaleString('vi-VN')} đ`}
              valueStyle={{ color: '#1a1e23', fontSize: 18 }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="Tổng KPI đã đạt"
              value={totals.totalActual}
              formatter={(v) => `${Number(v).toLocaleString('vi-VN')} đ`}
              valueStyle={{ color: '#16a34a', fontSize: 18 }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="% Trung bình hoàn thành"
              value={totals.avgPct.toFixed(1)}
              suffix="%"
              valueStyle={{ color: colorByPct(totals.avgPct) }}
            />
          </Card>
        </Col>
      </Row>

      {/* Action bar */}
      <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          icon={<FileSpreadsheet size={14} />}
          onClick={handleExport}
          disabled={rows.length === 0}
        >
          Xuất Excel
        </Button>
      </div>

      {/* Table */}
      <Card style={{ padding: 0 }}>
        {isLoading ? (
          <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 40 }}>
            <Empty description="Chưa có task nào có KPI doanh thu trong phạm vi đã chọn" />
          </div>
        ) : (
          <div>
            {/* Header */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '90px 2.5fr 1fr 1.2fr 1fr 1fr 1.2fr',
              gap: 8,
              padding: '10px 12px',
              fontSize: 11, fontWeight: 700, color: '#48535e',
              background: '#f5f7fa', borderBottom: '2px solid #e8edf2',
            }}>
              <div>Mã</div>
              <div>Tên task</div>
              <div>Khoa/phòng</div>
              <div>Người thực hiện</div>
              <div style={{ textAlign: 'right' }}>KPI Plan</div>
              <div style={{ textAlign: 'right' }}>KPI Actual</div>
              <div>Tiến độ</div>
            </div>
            {rows.map(r => (
              <div
                key={r.id}
                onClick={() => navigate(`/tasks/${r.id}`)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '90px 2.5fr 1fr 1.2fr 1fr 1fr 1.2fr',
                  gap: 8,
                  padding: '10px 12px',
                  fontSize: 12,
                  borderBottom: '1px solid #f0f2f5',
                  cursor: 'pointer',
                  alignItems: 'center',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#f5f7fa'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
              >
                <div style={{ fontFamily: 'monospace', color: '#1976D2', fontWeight: 600, fontSize: 11 }}>
                  {r.code}
                </div>
                <div>
                  <div style={{ fontWeight: 500, color: '#1a1e23' }}>{r.title}</div>
                  <div style={{ fontSize: 10, color: '#7d8a98', marginTop: 2 }}>
                    Hạn: {dayjs(r.dueDate).format('DD/MM/YYYY')}
                    {r.descendantCount > 0 && (
                      <> · {r.descendantCount} task con</>
                    )}
                  </div>
                </div>
                <div style={{ fontSize: 11, color: '#48535e' }}>{r.departmentName}</div>
                <div style={{ fontSize: 11, color: '#48535e' }}>
                  {r.assigneeName ?? <span style={{ color: '#bdc4cd' }}>—</span>}
                </div>
                <div style={{ textAlign: 'right', fontWeight: 600, color: '#1a1e23' }}>
                  {r.kpiRevenuePlan.toLocaleString('vi-VN')} đ
                </div>
                <div style={{ textAlign: 'right', fontWeight: 600, color: '#16a34a' }}>
                  {r.aggregatedKpiActual.toLocaleString('vi-VN')} đ
                </div>
                <div>
                  <Progress
                    percent={Math.min(100, r.progressPercent)}
                    strokeColor={colorByPct(r.progressPercent)}
                    size="small"
                    format={() => `${r.progressPercent.toFixed(1)}%`}
                  />
                  <div style={{ marginTop: 2 }}>
                    <Tag style={{ fontSize: 9, padding: '0 4px', lineHeight: '14px' }}>
                      {r.status}
                    </Tag>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </>
  );
}
